/**
 * ATTEST-EVIDENCE-INTEGRITY-1b — the evidence behind one statement.
 *
 * Before this, evidence was a sentence: the CPA typed "Form 1120S, FY2024" and
 * the engine took their word for it. A letter's value is that its statements
 * trace to something; a string nobody can open traces to nothing.
 *
 * So a source can now be a *file*. The upload goes through the parsers that
 * already exist, and what comes back is a blob id (the citation), a suggested
 * reference, and any figures the parser recognised.
 *
 * Two things this screen deliberately does not do:
 *
 * - It never writes a figure into a statement by itself. The figures are a
 *   menu; the CPA clicks one and it lands in *their* sentence, which they then
 *   edit. Nothing is auto-asserted.
 * - It never refuses. A document the parser can't read still attaches — a bank
 *   confirmation is perfectly good evidence with no income statement in it, and
 *   the amount gets typed by hand. Refusing is the server's job.
 *
 * Split out of `LetterDetailsStep.tsx` under docs/FILE_SIZE_POLICY.md.
 */
import { useState } from 'react'

import { uploadEvidence } from '@/components/attest/api'
import type { ParsedFigure } from '@/components/attest/api'
import type { EvidenceDraft } from '@/components/attest/formState'
import {
  EXAMPLE_PLACEHOLDER,
  RequiredMark,
} from '@/components/attest/requiredFields'
import { WIZARD_FIELD_HELP } from '@/components/attest/wizardHelp'
import { HelpDot } from '@/components/ui/help-dot'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { formatApiError } from '@/utils/httpErrors'

const SOURCE_TYPES = ['document', 'procedure', 'client_representation']

/** Accepted by `_upload_guards.ALLOWED_EXTENSIONS` — mirrored, not invented. */
const ACCEPT = '.pdf,.xlsx,.xls,.csv'

export interface EvidenceBlockProps {
  statementIndex: number
  evidence: EvidenceDraft[]
  onChange: (index: number, patch: Partial<EvidenceDraft>) => void
  onAdd: () => void
  onRemove: (index: number) => void
  /**
   * Append a figure to the statement's own sentence. The CPA edits from there.
   *
   * The evidence index rides along because the click is also a *claim about
   * this document*: the parent records which figure came out of which source,
   * and the server re-reads that document at build and confirms the key still
   * resolves to the same number (ATTEST-EVIDENCE-REQUIRED-1 item 3b).
   */
  onInsertFigure: (evidenceIndex: number, figure: ParsedFigure) => void
}

/** Currency, no cents: a letter states "$1,284,300", not "1284300.0". */
export function formatFigure(value: number): string {
  return value.toLocaleString('en-US', {
    style: 'currency', currency: 'USD', maximumFractionDigits: 0,
  })
}

export function EvidenceBlock({
  statementIndex: sIdx, evidence, onChange, onAdd, onRemove, onInsertFigure,
}: EvidenceBlockProps) {
  const [busy, setBusy] = useState<number | null>(null)
  const [errors, setErrors] = useState<Record<number, string>>({})

  const handleUpload = async (eIdx: number, file: File | undefined) => {
    if (!file) return
    setBusy(eIdx)
    setErrors(prev => ({ ...prev, [eIdx]: '' }))
    try {
      const result = await uploadEvidence(file)
      onChange(eIdx, {
        source_type: result.source_type || 'document',
        // The suggested citation is a starting point, not a citation: the CPA
        // is the one signing, and a classifier's guess is not their words.
        reference: result.reference,
        location: result.location,
        filename: result.filename,
        documentKind: result.document_kind,
        parsed: result.parsed,
        figures: result.figures,
        readNote: result.warnings.join(' '),
        // A new file on this row retires the old click. The key was read out of
        // the document being replaced, and carrying it forward would either name
        // a figure the new document has never heard of (a refusal the CPA cannot
        // account for) or, worse, collide with a same-named figure holding a
        // different number. The sentence keeps whatever they wrote — that text is
        // theirs — and the statement-level check still reads it.
        figure_key: '',
        figure_value: null,
      })
    } catch (err) {
      setErrors(prev => ({ ...prev, [eIdx]: formatApiError(err) }))
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="mt-3">
      <div className="flex items-center justify-between">
        {/* The asterisk hangs on the heading, not on a field: the row's inputs
            have `aria-label`s rather than `<Label>`s, and what is required is
            "at least one source with a reference", not any one box. The id
            matches the preflight blocker's (ATTEST-UX-1 D1). */}
        {/* The (i) rides on the first statement's block only — same copy on
            every row, and a repeated `data-testid` is a collision, not a hook. */}
        {/* `id` + `tabIndex` so "Correct here" can land on this block rather
            than on the step: a heading is not focusable by default, and this
            was the one blocker id with no element to scroll to (UX-2 D2). */}
        <p
          id={`statement-evidence-${sIdx}`}
          tabIndex={-1}
          className="flex items-center text-xs font-semibold uppercase tracking-wide text-muted-foreground"
        >
          Evidence
          <RequiredMark field={`statement-evidence-${sIdx}`} />
          {sIdx === 0 && <HelpDot {...WIZARD_FIELD_HELP.evidence} testId="info-evidence" className="ml-1" />}
        </p>
        <Button
          variant="ghost"
          size="sm"
          data-testid={`add-evidence-${sIdx}`}
          onClick={onAdd}
        >
          Add source
        </Button>
      </div>

      {evidence.map((item, eIdx) => (
        <div key={eIdx} className="mt-2 rounded-md border border-border p-3">
          <div className="grid gap-2 md:grid-cols-4">
            <select
              aria-label="Source type"
              data-testid={`evidence-type-${sIdx}-${eIdx}`}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
              value={item.source_type}
              onChange={e => onChange(eIdx, { source_type: e.target.value })}
            >
              {SOURCE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <Input
              aria-label="Reference"
              data-testid={`evidence-ref-${sIdx}-${eIdx}`}
              className={EXAMPLE_PLACEHOLDER}
              placeholder="e.g. Form 1120S, FY2024, filed 2025-03-14"
              value={item.reference}
              onChange={e => onChange(eIdx, { reference: e.target.value })}
            />
            <Input
              aria-label="Location"
              data-testid={`evidence-location-${sIdx}-${eIdx}`}
              className={EXAMPLE_PLACEHOLDER}
              placeholder="e.g. Workpaper B-3"
              value={item.location}
              onChange={e => onChange(eIdx, { location: e.target.value })}
            />
            <Input
              aria-label="Date obtained"
              type="date"
              value={item.obtained_date}
              onChange={e => onChange(eIdx, { obtained_date: e.target.value })}
            />
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-3">
            <label className="text-sm">
              <span className="cursor-pointer underline">
                {busy === eIdx ? 'Reading document…' : 'Attach document'}
              </span>
              <input
                type="file"
                accept={ACCEPT}
                data-testid={`evidence-file-${sIdx}-${eIdx}`}
                className="sr-only"
                disabled={busy === eIdx}
                onChange={e => {
                  const file = e.target.files?.[0]
                  // Clear the input so re-picking the same file still fires.
                  e.target.value = ''
                  void handleUpload(eIdx, file)
                }}
              />
            </label>
            {item.filename && (
              <span
                className="text-xs text-muted-foreground"
                data-testid={`evidence-filename-${sIdx}-${eIdx}`}
              >
                {item.filename}
                {item.documentKind && item.documentKind !== 'unknown'
                  && ` · read as ${item.documentKind.replace(/_/g, ' ')}`}
              </span>
            )}
            {evidence.length > 1 && (
              <Button
                variant="ghost"
                size="sm"
                data-testid={`remove-evidence-${sIdx}-${eIdx}`}
                onClick={() => onRemove(eIdx)}
              >
                Remove source
              </Button>
            )}
          </div>

          {errors[eIdx] && (
            <p
              className="mt-2 text-xs text-destructive"
              data-testid={`evidence-error-${sIdx}-${eIdx}`}
            >
              {errors[eIdx]}
            </p>
          )}

          {item.readNote && (
            <p className="mt-2 text-xs text-amber-700" data-testid={`evidence-note-${sIdx}-${eIdx}`}>
              {item.readNote}
            </p>
          )}

          {item.figures && item.figures.length > 0 && (
            <div className="mt-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Figures read from this document
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Click one to put it in the sentence above. Check it against the
                document before you sign — the figure is inserted, not verified.
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {item.figures.map(figure => (
                  <button
                    key={figure.key}
                    type="button"
                    data-testid={`evidence-figure-${sIdx}-${eIdx}-${figure.key}`}
                    className="rounded-md border border-input px-2 py-1 text-xs hover:bg-accent"
                    onClick={() => onInsertFigure(eIdx, figure)}
                  >
                    <span className="font-medium">{figure.label}</span>{' '}
                    <span className="text-muted-foreground">
                      {formatFigure(figure.value)}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}
